import dayjs from "dayjs";
import {
  BONUS_STATUS,
  BONUS_TYPES,
  BonusFreeSpinsMappedType,
  BonusMappedType,
  BonusNominalMappedType,
  BonusTriggerMappedType,
  FREE_SPINS_STAGES,
  TriggerFreeSpinMappedType,
} from "types/Bonus";
import {
  AvailableTriggerCompaingFreeSpinsType,
  AvailableTriggerCompaingStatusType,
  AvailableTriggerCompaingType,
} from "types/TriggerCompaings";
import {
  BonusFreeSpinResponseDataType,
  BonusNominalResponseDataType,
  BonusResponseDataType,
  FreeSpinResponseDataType,
} from "types/User";

export const mapBonusB2F = (bonus: BonusResponseDataType): BonusMappedType => {
  const expiredDate = dayjs(bonus.createdAt)
    .add(bonus.duration, "hours")
    .format();
  const progress = ((bonus.totalWager - bonus.wager) / bonus.totalWager) * 100;
  return {
    id: bonus.id,
    title: bonus.name,
    description: bonus.description,
    status: bonus.status,
    type: bonus.type,
    reward: bonus.sum,
    duration: bonus.duration,
    startDate: bonus.createdAt,
    expireDate: expiredDate,
    wager: bonus.wager,
    totalWager: bonus.totalWager,
    progress,
  };
};

export const mapBonusNominalB2F = (
  bonus: BonusNominalResponseDataType,
): BonusNominalMappedType => {
  const totalWagerAmount = bonus.wager * bonus.amount;
  const progress =
    ((totalWagerAmount - bonus.wagerAmountRemain) / totalWagerAmount) * 100;

  const getStatus = (b: BonusNominalResponseDataType): BONUS_STATUS => {
    const isExpired = dayjs(b.expireAt).isBefore(dayjs());
    if (isExpired) {
      return BONUS_STATUS.DEACTIVE;
    }
    return b.status;
  };

  return {
    ...bonus,
    status: getStatus(bonus),
    progress,
    type: BONUS_TYPES.NOMINAL,
  };
};

export const mapBonusFreeSpins = (
  bonus: BonusFreeSpinResponseDataType,
): BonusFreeSpinsMappedType => {
  const getStatus = (b: BonusFreeSpinResponseDataType): BONUS_STATUS => {
    const isExpired = dayjs(b.freeSpinsExpireAt).isBefore(dayjs());

    if (b.status === undefined) {
      return BONUS_STATUS.QUEUE;
    }
    if (isExpired) {
      return BONUS_STATUS.DEACTIVE;
    }
    return b.status;
  };

  const getStage = (): FREE_SPINS_STAGES => {
    if (bonus.status === BONUS_STATUS.QUEUE) {
      return FREE_SPINS_STAGES.QUEUE;
    }
    if (bonus.freeSpinsCountRemain > 0) {
      return FREE_SPINS_STAGES.UNUSED;
    }
    if (bonus.freeSpinsCountRemain === 0) {
      return FREE_SPINS_STAGES.USED;
    }
    return FREE_SPINS_STAGES.AVAILABLE;
  };

  const status = getStatus(bonus);
  const stage = getStage();

  const rewardAmount =
    "freeSpinsWinAmount" in bonus
      ? (bonus?.freeSpinsWinAmount * bonus?.wager) / 100
      : 0;

  const reward =
    "wagerAmountRemain" in bonus
      ? rewardAmount - bonus.wagerAmountRemain / 100
      : 0;

  const progress =
    stage === FREE_SPINS_STAGES.USED
      ? (reward / rewardAmount) * 100
      : ((bonus.freeSpinsCount - bonus.freeSpinsCountRemain) /
          bonus.freeSpinsCount) *
        100;

  return {
    ...bonus,
    progress,
    status,
    freeSpinStage: getStage(),
    type: BONUS_TYPES.FREE_SPINS,
  };
};

export const mapFreeSpinB2F = (
  bonus: FreeSpinResponseDataType,
): BonusMappedType => {
  const dateTimeFrom = bonus.startWagingDate
    ? dayjs(bonus.startWagingDate)
    : dayjs(bonus.validFrom);
  const dateTimeUntil = bonus.startWagingDate
    ? dayjs(bonus.startWagingDate).add(24, "hours")
    : dayjs(bonus.validUntil);
  const dateTimeNow = dayjs();

  const isActive = dateTimeNow >= dateTimeFrom && dateTimeNow <= dateTimeUntil;
  const status = isActive ? BONUS_STATUS.ACTIVE : BONUS_STATUS.DEACTIVE;

  const duration = bonus.startWagingDate
    ? 24
    : dateTimeUntil.diff(dateTimeFrom, "hours");
  const progress = ((bonus.totalWager - bonus.wager) / bonus.totalWager) * 100;

  return {
    id: Number(bonus.id),
    title: bonus.title || "",
    description: "",
    status,
    type: BONUS_TYPES.SPINS,
    game: bonus.game,
    leftSpins: bonus.leftQuantity,
    totalSpins: bonus.totalQuantity,
    duration,
    startDate: bonus.validFrom,
    expireDate: dateTimeUntil.format(),
    startWagingDate: bonus.startWagingDate,
    wager: bonus.wager,
    totalWager: bonus.totalWager,
    progress,
  };
};

const getCurrentStatusTriggerCompaing = (
  status: AvailableTriggerCompaingStatusType,
): BONUS_STATUS => {
  switch (status) {
    case AvailableTriggerCompaingStatusType.AVAILABLE:
      return BONUS_STATUS.AVAILABLE;
    case AvailableTriggerCompaingStatusType.AVAILABLE_QUEUE:
      return BONUS_STATUS.AVAILABLE_QUEUE;
    case AvailableTriggerCompaingStatusType.UNAVAILABLE:
      return BONUS_STATUS.UNAVAILABLE;
    default:
      return BONUS_STATUS.UNAVAILABLE;
  }
};

export const mapAvailableTriggerB2F = (
  trigger: AvailableTriggerCompaingType,
): BonusTriggerMappedType => {
  return {
    ...trigger,
    status: getCurrentStatusTriggerCompaing(trigger.status),
    type: BONUS_TYPES.TRIGGER,
  };
};

export const mapTriggersFreeSpins = (
  bonus: AvailableTriggerCompaingFreeSpinsType,
): TriggerFreeSpinMappedType => {
  return {
    ...bonus,
    freeSpinStage: FREE_SPINS_STAGES.AVAILABLE,
    status: getCurrentStatusTriggerCompaing(bonus.status),
    type: BONUS_TYPES.FREE_SPINS,
  };
};
